import { type NavigateOptions, useSearchParams } from "@solidjs/router";

export const useTypedSearchParams = <
	T extends Record<string, string | number | null | undefined>
>(): [{ [K in keyof T]: T[K] }, (params: T, options?: Partial<NavigateOptions>) => void] => {
	const [searchParams, setSearchParams] = useSearchParams();

	return [searchParams as { [K in keyof T]: T[K] }, setSearchParams];
};

export const constructLinkWithParams = <P extends Record<string, string | number>>(
	url: string,
	params: P
): URL => {
	const out = new URL(`${window.location.origin}${url}`);

	for (const [key, val] of Object.entries(params)) {
		out.searchParams.set(key, val.toString());
	}
	return out;
};

/**
 * @description Validates a string value against a set of valid values (enum or array)
 * @param value - The value to validate
 * @param validValues - Record/enum object or array of valid values
 * @param defaultValue - The default value to return if validation fails
 * @returns The validated value or default value
 */
export const validateEnumValue = <T extends string>(
	value: string | undefined,
	validValues: Record<string, T> | readonly T[],
	defaultValue: T
): T => {
	if (!value) return defaultValue;

	const values = Array.isArray(validValues) ? validValues : (Object.values(validValues) as T[]);

	return values.includes(value as T) ? (value as T) : defaultValue;
};
